/**
 * Application version information
 *
 * Version data is injected at build time from Git tags.
 * Use getAppVersion() to access version info throughout the app.
 */

export interface AppVersion {
  /** Semantic version from Git tag (e.g., "0.1.0") */
  version: string;
  /** Short Git commit hash (e.g., "e15759e") */
  commit: string;
  /** ISO timestamp of when the build was created */
  buildDate: string;
}

/**
 * Get the current application version information.
 *
 * Values are injected at build time via Vite's define config.
 * Falls back to defaults if not available (e.g., in development without tags).
 */
export function getAppVersion(): AppVersion {
  return {
    version: import.meta.env.VITE_APP_VERSION || '0.0.0',
    commit: import.meta.env.VITE_GIT_COMMIT || 'dev',
    buildDate: import.meta.env.VITE_BUILD_DATE || new Date().toISOString(),
  };
}

/**
 * Get a formatted version string for display (e.g., "v0.1.0")
 */
export function getVersionDisplay(): string {
  const { version } = getAppVersion();
  return `v${version}`;
}

/**
 * Get a full version string with commit (e.g., "v0.1.0 (e15759e)")
 */
export function getFullVersionDisplay(): string {
  const { version, commit } = getAppVersion();
  return `v${version} (${commit})`;
}
